import { Euler, Matrix4, Quaternion, Vector3, MathUtils } from 'three';
import forwardProfile from './forwardWalkingProfile.json';
import backwardProfile from './backwardWalkingProfile.json';
import leftSideStepProfile from './leftSideStepProfile.json';
import rightSideStepProfile from './rightSideStepProfile.json';
import curveRightProfile from './curveRightWalkingProfile.json';
import curveLeftProfile from './curveLeftWalkingProfile.json';
import {
  getMotionDurationMs, motionToTimeline, interpolate,
  type MoveTrackId, type TimelineData, type WegoMotion, type WegoMotionFrame,
} from './animationUtils';

export type WalkingFoot = 'LEFT' | 'RIGHT';
export type StrideDirection = 'forward' | 'backward';
export type SideStepDirection = 'left' | 'right';
export type CurveDirection = 'curveRight' | 'curveLeft';
export type WalkingDirection = StrideDirection | SideStepDirection | CurveDirection;
export const WALKING_TIMELINE_FPS = 1000;
export const MAX_WALKING_STEPS = 30;
const SAMPLE_MS = 10;
const SIDE_STEP_SUPPORT_LAG_MS = 40;
// About 1 cm per repetition on the real robot (world units are 10x metres).
const SIDE_STEP_TRAVEL_PER_CYCLE = 0.1;
// The legs have no yaw joint, so the turn comes from foot slip on the real robot; assumed 45° per five steps.
export const CURVE_YAW_PER_STEP_DEGREES = 9;
const MODEL_SCALE = 10;
const profile = forwardProfile;
const strideGaits = {
  forward: { profile: forwardProfile, motionNo: 4, label: '前進' },
  backward: { profile: backwardProfile, motionNo: 5, label: '後進' },
} as const;
const sideStepGaits = {
  left: { profile: leftSideStepProfile, motionNo: 1002, label: '左に移動' },
  right: { profile: rightSideStepProfile, motionNo: 1006, label: '右に移動' },
} as const;
// Positive yaw turns the robot to its left.
const curveGaits = {
  curveRight: { profile: curveRightProfile, motionNo: 1009, label: '右カーブ歩行', yawSign: -1 },
  curveLeft: { profile: curveLeftProfile, motionNo: 1010, label: '左カーブ歩行', yawSign: 1 },
} as const;
const gaitLabels: Record<WalkingDirection, string> = {
  forward: '前進', backward: '後進', left: '左に移動', right: '右に移動', curveRight: '右カーブ歩行', curveLeft: '左カーブ歩行',
};
const isSideStep = (direction: WalkingDirection): direction is SideStepDirection => direction in sideStepGaits;
const isCurve = (direction: WalkingDirection): direction is CurveDirection => direction in curveGaits;
const up = new Vector3(0, 1, 0);
const verticalAxis = new Vector3(0, 0, 1);
const worldRotation = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), Math.PI / 2);
const pairs = [
  ['LEFT_SHOULDER', 'RIGHT_SHOULDER'],
  ['LEFT_HIP', 'RIGHT_HIP'],
  ['LEFT_ANKLE_PITCH', 'RIGHT_ANKLE_PITCH'],
  ['LEFT_ANKLE_ROLL', 'RIGHT_ANKLE_ROLL'],
] as const;

export const validateWalkingSteps = (steps: number) => {
  if (!Number.isSafeInteger(steps) || steps < 1 || steps > MAX_WALKING_STEPS) {
    throw new Error(`歩数は1〜${MAX_WALKING_STEPS}の整数で指定してください。`);
  }
};

export const mirrorWalkingJoints = (joints: Record<string, number>): Record<string, number> => {
  const mirrored = { ...joints };
  pairs.forEach(([left, right]) => {
    mirrored[left] = -joints[right];
    mirrored[right] = -joints[left];
  });
  return mirrored;
};

export const getWalkingDirection = (motionNo: number): WalkingDirection | undefined => (
  ({ 4: 'forward', 5: 'backward', 1002: 'left', 1006: 'right', 1009: 'curveRight', 1010: 'curveLeft' } as const)[motionNo]
);

export const getWalkingDirectionLabel = (direction: WalkingDirection) => gaitLabels[direction];
export const getWalkingStepUnit = (direction: WalkingDirection) => (isSideStep(direction) ? '回' : '歩');
export const getWalkingStepsLabel = (direction: WalkingDirection, steps: number) => (
  `${gaitLabels[direction]} ${steps}${getWalkingStepUnit(direction)}`
);

const sumDurations = (frames: readonly WegoMotionFrame[]) => frames.reduce((total, frame) => total + frame.durationMs, 0);

const gaitTiming = (direction: StrideDirection) => {
  const source = strideGaits[direction].profile;
  return {
    startMs: sumDurations(source.start),
    strideMs: sumDurations(source.left),
    footfallMs: source.left[0].durationMs,
  };
};

type WalkingPhase = { startMs: number; durationMs: number };

/** Counted strides followed by the closing phase; curve strides alternate between two lengths. */
const stridePhases = (direction: StrideDirection | CurveDirection, steps: number) => {
  const durations = (() => {
    if (!isCurve(direction)) {
      const { strideMs } = gaitTiming(direction);
      const source = strideGaits[direction].profile;
      return [
        ...Array.from({ length: steps }, () => strideMs),
        sumDurations(steps % 2 ? source.finishRight : source.finishLeft),
      ];
    }
    const source = curveGaits[direction].profile;
    return [
      ...Array.from({ length: steps }, (_, index) => sumDurations(index % 2 ? source.second : source.first)),
      sumDurations(steps % 2 ? source.finishAfterFirst : source.finishAfterSecond),
    ];
  })();
  const source = isCurve(direction) ? curveGaits[direction].profile : strideGaits[direction].profile;
  let startMs = sumDurations(source.start);
  const phases: WalkingPhase[] = durations.map((durationMs) => {
    const phase = { startMs, durationMs };
    startMs += durationMs;
    return phase;
  });
  return { startMs: phases[0].startMs, phases };
};

export const createWalkingMotion = (direction: WalkingDirection, steps: number): WegoMotion => {
  validateWalkingSteps(steps);
  const name = getWalkingStepsLabel(direction, steps);
  if (isSideStep(direction)) {
    const { profile: source, motionNo } = sideStepGaits[direction];
    const frames = [...source.start, ...Array.from({ length: steps }, () => source.cycle).flat(), ...source.finish];
    return { motionNo, name, frames: frames.map(cloneFrame) };
  }
  if (isCurve(direction)) {
    const { profile: source, motionNo } = curveGaits[direction];
    const frames: WegoMotionFrame[] = [...source.start];
    for (let step = 1; step <= steps; step += 1) frames.push(...(step % 2 ? source.first : source.second));
    frames.push(...(steps % 2 ? source.finishAfterFirst : source.finishAfterSecond));
    return { motionNo, name, frames: frames.map(cloneFrame) };
  }
  const { profile: source, motionNo } = strideGaits[direction];
  const frames: WegoMotionFrame[] = source.start.map(cloneFrame);
  for (let step = 1; step <= steps; step += 1) {
    frames.push(...(step % 2 ? source.left : source.right).map(cloneFrame));
  }
  frames.push(...(steps % 2 ? source.finishRight : source.finishLeft).map(cloneFrame));
  return { motionNo, name, frames };
};

export const createForwardWalkingMotion = (steps: number) => createWalkingMotion('forward', steps);

const cloneFrame = (frame: WegoMotionFrame): WegoMotionFrame => ({
  ...frame, jointsDegrees: { ...frame.jointsDegrees }, led: frame.led ? { ...frame.led } : null,
  jointSpeeds: frame.jointSpeeds ? { ...frame.jointSpeeds } : undefined,
});

export const getWalkingFootGeometry = (joints: Record<string, number>, foot: WalkingFoot) => {
  const leg = profile.legs[foot];
  const matrix = new Matrix4();
  leg.joints.forEach((joint) => {
    // URDF uses fixed-axis RPY, followed by the rotation about the joint-local axis.
    const origin = new Quaternion().setFromEuler(new Euler(...tuple(joint.rpy), 'ZYX'));
    const rotation = new Quaternion().setFromAxisAngle(vector(joint.axis), MathUtils.degToRad(joints[joint.id]));
    matrix.multiply(new Matrix4().compose(vector(joint.xyz), origin.multiply(rotation), new Vector3(1, 1, 1)));
  });
  const corners: Vector3[] = [];
  for (const x of [-1, 1]) for (const y of [-1, 1]) for (const z of [-1, 1]) {
    corners.push(new Vector3(
      leg.footCenter[0] + x * leg.footSize[0] / 2,
      leg.footCenter[1] + y * leg.footSize[1] / 2,
      leg.footCenter[2] + z * leg.footSize[2] / 2,
    ).applyMatrix4(matrix));
  }
  return {
    corners,
    center: vector(leg.footCenter).applyMatrix4(matrix),
    normal: up.clone().transformDirection(matrix),
  };
};

const tuple = (values: number[]): [number, number, number] => [values[0], values[1], values[2]];
const vector = (values: number[]) => new Vector3(...tuple(values));
const smooth = (value: number) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};

/** Weight on the left foot; mirrored when the gait starts with the right foot. */
const supportLeftWeight = (timeMs: number, startMs: number, phases: WalkingPhase[], firstFootLeft: boolean) => {
  const weight = (() => {
    if (timeMs <= startMs) return 0.5 * (1 - smooth((timeMs - 400) / 400));
    const found = phases.findIndex(phase => timeMs < phase.startMs + phase.durationMs);
    const index = found < 0 ? phases.length - 1 : found;
    const phase = phases[index];
    const progressMs = timeMs - phase.startMs;
    const transfer = index === phases.length - 1
      ? 0.5 * smooth(progressMs / 600)
      : smooth((progressMs - 300) / (phase.durationMs - 350));
    return index % 2 === 0 ? transfer : 1 - transfer;
  })();
  return firstFootLeft ? weight : 1 - weight;
};

/** Curve heading in degrees: each counted stride turns smoothly by the same amount. */
const curveYawAt = (timeMs: number, phases: WalkingPhase[], yawSign: number) => {
  const counted = phases.slice(0, -1);
  const turned = counted.reduce((total, phase) => (
    total + smooth((timeMs - phase.startMs) / phase.durationMs)
  ), 0);
  return yawSign * CURVE_YAW_PER_STEP_DEGREES * turned;
};

export const createWalkingTimeline = (direction: WalkingDirection, steps: number): TimelineData => {
  const motion = createWalkingMotion(direction, steps);
  const timeline = motionToTimeline(motion, WALKING_TIMELINE_FPS);
  timeline.nativeWalking = true;
  // Side steps only roll the ankles, so support smoothly follows the foot that is in contact.
  let smoothedWeight = 0.5;
  let previousTime = 0;
  let supportWeight = (time: number, contactWeight: number) => {
    smoothedWeight += (contactWeight - smoothedWeight) * (1 - Math.exp(-(time - previousTime) / SIDE_STEP_SUPPORT_LAG_MS));
    previousTime = time;
    return smoothedWeight;
  };
  let yawAt: (time: number) => number = () => 0;
  if (isSideStep(direction)) {
    timeline.nativeAudioCues = [];
  } else {
    const { startMs, phases } = stridePhases(direction, steps);
    if (isCurve(direction)) {
      const { profile: source, yawSign } = curveGaits[direction];
      const phaseSounds = [
        ...Array.from({ length: steps }, (_, index) => (index % 2 ? source.sounds.second : source.sounds.first)),
        steps % 2 ? source.sounds.finishAfterFirst : source.sounds.finishAfterSecond,
      ];
      timeline.nativeAudioCues = phaseSounds.flatMap((cues, index) => cues.map(cue => ({
        timeMs: phases[index].startMs + cue.timeMs, kind: cue.kind === 'voice' ? 'voice' as const : 'sound' as const,
        id: cue.id, volume: cue.volume,
      })));
      yawAt = time => curveYawAt(time, phases, yawSign);
    } else {
      const { strideMs, footfallMs } = gaitTiming(direction);
      const { footfallSounds } = strideGaits[direction].profile;
      timeline.nativeAudioCues = Array.from({ length: steps + 1 }, (_, index) => {
        const sound = footfallSounds[index % 2];
        if (sound.kind !== 'sound') throw new Error('歩行音のプロファイルが不正です。');
        return { timeMs: startMs + index * strideMs + footfallMs, kind: 'sound', id: sound.id, volume: sound.volume };
      });
    }
    const firstFootLeft = !isCurve(direction) || curveGaits[direction].profile.firstFoot === 'LEFT';
    supportWeight = time => supportLeftWeight(time, startMs, phases, firstFootLeft);
  }
  const durationMs = getMotionDurationMs(motion);
  const times = new Set<number>([0, durationMs]);
  timeline.joints.forEach(track => track.keyframes.forEach(key => times.add(key.frame)));
  // Side steps swing the ankle roll about 40° within 60 ms, so they need a finer contact sampling.
  const sampleMs = isSideStep(direction) ? SAMPLE_MS / 2 : SAMPLE_MS;
  for (let time = sampleMs; time < durationMs; time += sampleMs) times.add(time);
  const movement = new Map<MoveTrackId, { frame: number; value: number }[]>();
  const ids: MoveTrackId[] = ['x', 'y', 'z', 'yaw', 'bodyRotationX', 'bodyRotationY', 'bodyRotationZ'];
  ids.forEach(id => movement.set(id, []));
  let previous: { left: Vector3[]; right: Vector3[] } | null = null;
  let previousWeight = 0.5;
  const position = new Vector3();

  [...times].sort((a, b) => a - b).forEach(time => {
    const joints = Object.fromEntries(timeline.joints.map(track => [track.id, interpolate(track.keyframes, time)]));
    const left = getWalkingFootGeometry(joints, 'LEFT');
    const right = getWalkingFootGeometry(joints, 'RIGHT');
    const leftWeight = supportWeight(time, previousWeight);
    const normal = right.normal.clone().lerp(left.normal, leftWeight).normalize();
    const body = new Quaternion().setFromUnitVectors(normal, up);
    const finishStart = durationMs - 360;
    if (time > finishStart) body.slerp(new Quaternion(), smooth((time - finishStart) / 180));
    const world = worldRotation.clone().multiply(body);
    const worldCorners = (corners: Vector3[]) => corners.map(corner => corner.applyQuaternion(world).multiplyScalar(MODEL_SCALE));
    const leftCorners = worldCorners(left.corners);
    const rightCorners = worldCorners(right.corners);
    const leftZ = Math.min(...leftCorners.map(corner => corner.z));
    const rightZ = Math.min(...rightCorners.map(corner => corner.z));
    const contactWeight = smooth(0.5 + (rightZ - leftZ) / 0.004);
    if (previous) {
      const weight = (contactWeight + previousWeight) / 2;
      const contactDelta = (before: Vector3[], after: Vector3[], minimum: number) => {
        const indices = after.flatMap((corner, index) => corner.z <= minimum + 1e-6 ? [index] : []);
        const delta = new Vector3();
        indices.forEach(index => delta.add(before[index].clone().sub(after[index])));
        return delta.divideScalar(indices.length);
      };
      position.add(contactDelta(previous.left, leftCorners, leftZ).multiplyScalar(weight));
      position.add(contactDelta(previous.right, rightCorners, rightZ).multiplyScalar(1 - weight));
    }
    const lowestZ = Math.min(leftZ, rightZ);
    position.z = -lowestZ;
    const euler = new Euler().setFromQuaternion(body, 'XYZ');
    const values: Record<MoveTrackId, number> = {
      x: position.x, y: position.z, z: position.y, yaw: yawAt(time),
      bodyRotationX: MathUtils.radToDeg(euler.x),
      bodyRotationY: MathUtils.radToDeg(euler.y),
      bodyRotationZ: MathUtils.radToDeg(euler.z),
    };
    ids.forEach(id => movement.get(id)!.push({ frame: time, value: values[id] }));
    previous = { left: leftCorners, right: rightCorners };
    previousWeight = contactWeight;
  });
  if (isSideStep(direction)) applySideStepTravel(movement.get('x')!, direction, steps);
  if (isCurve(direction)) applyCurveTravel(movement, direction, steps);
  timeline.movement = timeline.movement.map(track => ({ ...track, keyframes: movement.get(track.id)! }));
  return timeline;
};

// Without foot slip the contact model turns the curve's hip swing into backward travel, while the real robot
// pivots instead. The curve therefore follows the forward gait's travel phase by phase, turned by the heading.
const applyCurveTravel = (
  movement: Map<MoveTrackId, { frame: number; value: number }[]>,
  direction: CurveDirection,
  steps: number,
) => {
  const forward = createWalkingTimeline('forward', steps).movement;
  const forwardKeys = (id: MoveTrackId) => forward.find(track => track.id === id)!.keyframes;
  const curve = stridePhases(direction, steps);
  const straight = stridePhases('forward', steps);
  const forwardTime = (time: number) => {
    if (time <= curve.startMs) return time * straight.startMs / curve.startMs;
    const found = curve.phases.findIndex(phase => time < phase.startMs + phase.durationMs);
    const index = found < 0 ? curve.phases.length - 1 : found;
    const from = curve.phases[index];
    const to = straight.phases[index];
    return to.startMs + Math.min(1, (time - from.startMs) / from.durationMs) * to.durationMs;
  };
  const xKeys = movement.get('x')!;
  const zKeys = movement.get('z')!;
  const yawKeys = movement.get('yaw')!;
  const position = new Vector3();
  let previous = new Vector3();
  // The forward gait starts with the left foot, so its sway is mirrored for a right-foot start.
  const sway = curveGaits[direction].profile.firstFoot === 'LEFT' ? 1 : -1;
  xKeys.forEach((key, index) => {
    const time = forwardTime(key.frame);
    const local = new Vector3(sway * interpolate(forwardKeys('x'), time), interpolate(forwardKeys('z'), time), 0);
    position.add(local.clone().sub(previous).applyAxisAngle(verticalAxis, MathUtils.degToRad(yawKeys[index].value)));
    previous = local;
    key.value = position.x;
    zKeys[index].value = position.y;
  });
};

// The real robot travels by its feet slipping when they land after the quick ankle-roll snap, which the
// rigid contact model cannot reproduce. Each cycle therefore slides a fixed distance during that landing,
// and the model's residual drift is cancelled so the robot only moves at those moments.
const applySideStepTravel = (keys: { frame: number; value: number }[], direction: SideStepDirection, steps: number) => {
  const { start, cycle } = sideStepGaits[direction].profile;
  const startMs = sumDurations(start);
  const cycleMs = sumDurations(cycle);
  const slideStartMs = cycle[0].durationMs;
  const slideMs = cycle[1].durationMs + cycle[2].durationMs;
  const travel = (direction === 'left' ? 1 : -1) * SIDE_STEP_TRAVEL_PER_CYCLE;
  const valueAt = new Map(keys.map(key => [key.frame, key.value]));
  const endMs = keys.at(-1)!.frame;
  const segments = [
    { begin: 0, end: startMs, target: 0, weight: (t: number) => smooth(t / startMs) },
    ...Array.from({ length: steps }, (_, index) => {
      const begin = startMs + index * cycleMs;
      return { begin, end: begin + cycleMs, target: travel, weight: (t: number) => smooth((t - begin - slideStartMs) / slideMs) };
    }),
    { begin: startMs + steps * cycleMs, end: endMs, target: 0, weight: (t: number) => smooth((t - startMs - steps * cycleMs) / (endMs - startMs - steps * cycleMs)) },
  ].map(segment => ({ ...segment, correction: segment.target - (valueAt.get(segment.end)! - valueAt.get(segment.begin)!) }));
  keys.forEach((key) => {
    let offset = 0;
    for (const segment of segments) {
      if (key.frame >= segment.end) offset += segment.correction;
      else {
        if (key.frame > segment.begin) offset += segment.correction * segment.weight(key.frame);
        break;
      }
    }
    key.value += offset;
  });
};

export const createForwardWalkingTimeline = (steps: number) => createWalkingTimeline('forward', steps);
